import eventApi from "@/api/events";
import securityApi from "@/api/security";
import * as actions from "../types";
import state from "../state";
import {
  canBePositionable,
  isInsideWorkContract,
  canBeSimultaneous,
  endAfterMidnight,
  getSimultaneousTimeSlot,
  needLevel,
} from "@/tools/eventHelper";
import { generateDateIntoInterval, getEffectiveYears } from "@/tools/dateUtils";
import axios from "axios";
import { KronosError } from "@/tools/KronosError";
import i18n from "@/plugins/i18n.js";
import { isNullish } from "remeda";
import { nextTick } from "vue";

const currentLocale = i18n.locale;

export default {
  [actions.AUTH_REQUEST_SSO]: async ({ commit, dispatch }, { code }) => {
    const response = await securityApi.getJsonToken(code);
    commit(actions.AUTH_SUCCESS, response);
    await dispatch(actions.AUTH_INIT);
  },
  [actions.AUTH_REQUEST]: async (
    { commit, dispatch },
    { username, password }
  ) => {
    const response = await securityApi.getToken(username, password);
    commit(actions.AUTH_SUCCESS, response);
    await dispatch(actions.AUTH_INIT);
  },
  [actions.AUTH_REFRESH]: async ({ commit, dispatch, state }) => {
    const refreshToken = state.security.refreshToken;
    commit(actions.AUTH_REMOVE_REFRESH);
    const response = await securityApi.refreshToken(refreshToken);
    commit(actions.AUTH_SUCCESS, response);
    await dispatch(actions.AUTH_INIT);
  },
  [actions.AUTH_LOGOUT]: ({ commit }) => {
    commit(actions.AUTH_LOGOUT);
    delete axios.defaults.headers.common["Authorization"];
    localStorage.removeItem("user-token");
    localStorage.removeItem("refresh-token");
    localStorage.removeItem("expire-at-token");
    localStorage.removeItem("impersonate");
  },
  [actions.AUTH_INIT]: async ({ commit, state }) => {
    axios.defaults.headers.common["Authorization"] =
      "Bearer " + state.security.token;

    // Customer already loaded, return
    if ("id" in state.customer) {
      return;
    }

    let response = await securityApi.getInfosUser();
    commit(actions.AUTH_INIT, response.data);
  },
  [actions.UPDATE_USER]: async ({ commit, state }) => {
    axios.defaults.headers.common["Authorization"] =
      "Bearer " + state.security.token;

    const response = await securityApi.getInfosUser();
    commit(actions.AUTH_INIT, response.data);
  },
  [actions.BLOCK_TIMESLOT_EDITION]: ({ commit }, value) => {
    commit(actions.BLOCK_TIMESLOT_EDITION, value);
  },
  [actions.READ_NOTIFICATION]: async ({ commit }, notification) => {
    await securityApi.readNotification(notification.id);
    commit(actions.READ_NOTIFICATION, notification);
  },
  [actions.SET_DOMICILE]: ({ commit }, domicile) => {
    commit(actions.SET_DOMICILE, domicile);

    return securityApi.setDomicile(domicile);
  },
  [actions.SET_LOADING]: ({ commit }, isLoading) => {
    commit(actions.SET_LOADING, isLoading);
  },
  [actions.SET_IMPERSONATE]: ({ commit, state }, username) => {
    axios.defaults.headers.common["x-switch-user"] = username;

    const isAdmin = state.customer.roles.includes("ROLE_ADMIN");

    const impersonate = {
      username: username,
      admin: isAdmin,
    };

    localStorage.setItem("impersonate", JSON.stringify(impersonate));
    commit(actions.SET_IMPERSONATE, impersonate);
  },
  [actions.UNSET_IMPERSONATE]: ({ commit }) => {
    delete axios.defaults.headers.common["x-switch-user"];
    localStorage.removeItem("impersonate");
    commit(actions.UNSET_IMPERSONATE);
  },
  [actions.APPLY_FILTERS]({ commit }, filters) {
    commit(actions.APPLY_FILTERS, filters);
  },
  [actions.APPLY_AGENDA_FILTERS]({ commit }, filters) {
    commit(actions.APPLY_AGENDA_FILTERS, filters);
  },
  [actions.ARCHIVE_EVENT]({ commit }, eventId) {
    commit(actions.ARCHIVE_EVENT, eventId);
    return eventApi.archiveEvent(eventId, currentLocale);
  },
  [actions.FETCH_CALENDAR_EVENTS]({ commit }, { id, from, to }) {
    return eventApi
      .getCalendarFilteredEvents(id, from, to, currentLocale)
      .then((response) =>
        response.data.forEach(function (newEvent) {
          let timeSlots = newEvent["time_slots"];
          commit(actions.ADD_EVENT, newEvent);
          timeSlots.forEach((timeSlot) => {
            try {
              checkSimultaneousTimeSlot(commit, timeSlot);
            } catch (error) {
              if (error.response) {
                throw new KronosError(error.response.data.message);
              }
              throw error;
            }
          });
        })
      );
  },
  [actions.CANCEL_PLAN_EVENT]({ commit }) {
    commit(actions.CANCEL_PLAN_EVENT);
  },

  [actions.CONFIRM_SELECTED_EVENT]: async (
    { commit },
    { eventId, parentOrderLineNumber = null }
  ) => {
    let response;
    try {
      response = await eventApi.confirmEvent(
        eventId,
        parentOrderLineNumber,
        currentLocale
      );
    } catch (e) {
      console.error(e);
      throw e.response.data.message;
    }
    commit(actions.UPDATE_EVENT, response.data);
  },
  [actions.CLEAR_AGENDA_FILTER]({ commit }) {
    commit(actions.CLEAR_AGENDA_FILTER);
  },
  [actions.CLEAR_PLAN_EVENT]: async ({ commit }) => {
    commit(actions.CLEAR_PLAN_EVENT);

    await nextTick();
  },
  [actions.CREATE_EVENT]: async ({ commit }, newEvent) => {
    let simultaneousTimeSlots = [];

    if (!isInsideWorkContract(newEvent.timeSlot)) {
      throw i18n.t("messages.error.notInsideWorkContract");
    }

    if (Array.isArray(newEvent.timeSlot.date)) {
      let dates = newEvent.timeSlot.date;
      let timeSlots = [];

      if (newEvent.timeSlot.date.length === 2) {
        dates = generateDateIntoInterval(dates, state.holidays);
      }

      dates.forEach((date) => {
        timeSlots.push({ ...newEvent.timeSlot, ["date"]: date });
      });

      newEvent.timeSlots = timeSlots;
    } else {
      let simultaneousTimeSlots = getSimultaneousTimeSlot(newEvent.timeSlot);

      simultaneousTimeSlots.forEach((simultaneousTs) => {
        if (!canBeSimultaneous(newEvent, state.items[simultaneousTs.eventId])) {
          throw i18n.t("messages.error.eventPlacement");
        }
      });
    }

    try {
      let response = await eventApi.postUserEvent(newEvent, currentLocale);
      commit(actions.ADD_EVENT, { ...response.data });

      // if newEvent as several TimeSlots, it is Hr, so not simultaneous. no need to check
      if (!("timeSlots" in newEvent)) {
        let newTimeSlot = response.data["time_slots"][0];
        simultaneousTimeSlots.forEach((simultaneousTs) => {
          commit(actions.DEFINE_SIMULTANEOUS_TIME_SLOT, {
            ts1: newTimeSlot,
            ts2: simultaneousTs,
          });
        });
      }
    } catch (error) {
      if (error.response) {
        throw new KronosError(error.response.data.message);
      }
      throw error;
    }
  },
  [actions.CREATE_EVENT_TIME_SLOT]({ commit }, eventId) {
    commit(actions.CREATE_EVENT_TIME_SLOT, eventId);
  },
  [actions.DO_EVENT]: async ({ commit }, id) => {
    try {
      const { data } = await eventApi.doEvent(id, currentLocale);

      commit(actions.ARCHIVE_EVENT, id);
      commit(actions.ADD_EVENT, data);
    } catch (error) {
      throw error?.response?.data?.message;
    }
  },
  [actions.FETCH_CREDIT]: async ({ commit }, { year, calendarOwnerId }) => {
    const { data } = await eventApi.getCredits(
      year,
      calendarOwnerId,
      currentLocale
    );
    commit(actions.FETCH_CREDIT, data);
  },
  [actions.FETCH_REST_WORK_TIME]: async ({ commit }, year) => {
    const { data } = await eventApi.getRestWorkTime(year, currentLocale);
    commit(actions.FETCH_REST_WORK_TIME, data);
  },
  [actions.FETCH_TO_BE_PLANNED_EVENTS]: async ({ commit }) => {
    let { data } = await eventApi.getToBePlannedEvents(currentLocale);
    data.forEach(function (newEvent) {
      commit(actions.ADD_EVENT, newEvent);
    });
  },
  [actions.LOAD_CALENDAR_OWNERS]: async ({ commit }) => {
    const { data } = await eventApi.getCalendarOwners(currentLocale);
    commit(actions.LOAD_CALENDAR_OWNERS, data);
  },
  [actions.LOAD_CALENDAR_OWNER]: async ({ commit }, id) => {
    const { data } = await eventApi.getCalendarOwner(id, currentLocale);
    commit(actions.LOAD_CALENDAR_OWNER, data);
  },
  [actions.LOAD_EVENT_TYPE]: async ({ commit }) => {
    if (state.eventTypes) {
      return;
    }

    const { data } = await eventApi.geteventTypes(currentLocale);
    commit(actions.LOAD_EVENT_TYPE, data);
    localStorage.setItem("event-types", JSON.stringify(state.eventTypes));
  },
  [actions.LOAD_EVENT_TYPE_GROUP]: async ({ commit }) => {
    if (state.eventTypesGroup) {
      return;
    }

    const { data } = await eventApi.geteventTypesGroup(currentLocale);
    commit(actions.LOAD_EVENT_TYPE_GROUP, data);
    localStorage.setItem(
      "event-types-group",
      JSON.stringify(state.eventTypesGroup)
    );
  },
  [actions.REMOVE_AGENDA_EVENT_FILTER]({ commit }, filter) {
    commit(actions.REMOVE_AGENDA_EVENT_FILTER, filter);
  },
  [actions.REMOVE_EVENT_FILTER]({ commit }, filterName) {
    commit(actions.REMOVE_EVENT_FILTER, filterName);
  },
  [actions.SET_TO_BE_PLANNED_NEW_ORDER]({ commit }, events) {
    let newOrder = events.map((e) => e.id);
    commit(actions.SET_TO_BE_PLANNED_NEW_ORDER, newOrder);
  },
  [actions.SCHEDULE_MULTI_TIMESLOT]: async (
    { commit, state },
    { id, timeSlots, quantity, picking }
  ) => {
    timeSlots.forEach((timeSlot) =>
      checkSimultaneousTimeSlot(commit, timeSlot)
    );
    let eventTimeSlots = timeSlots.map((ts) => {
      let tsObj = {
        datetime: ts.date + " " + ts.time,
        duration: ts.duration,
      };

      if (ts["break_duration"] !== undefined) {
        tsObj["break_duration"] = ts["break_duration"];
      }

      if (ts["at_home"] !== undefined) {
        tsObj["at_home"] = ts["at_home"];
      }

      return tsObj;
    });

    if (picking) {
      try {
        if (!state.items[id].parentId) {
          let response = await eventApi.postPickedEvent(id, currentLocale);
          //change reference id ;
          id = response.data.id;
        }
        const resp = await eventApi.scheduleMultiTimeSlot(
          id,
          eventTimeSlots,
          quantity,
          currentLocale
        );

        if (resp.data["parentId"]) {
          commit(actions.ARCHIVE_EVENT, resp.data["parentId"]);
        }

        commit(actions.ARCHIVE_EVENT, id);
        let timeSlots = resp.data["time_slots"];
        commit(actions.ADD_EVENT, resp.data);
        timeSlots.forEach((timeSlot) => {
          checkSimultaneousTimeSlot(commit, timeSlot);
        });
        //duration calculation
        commit(actions.UPDATE_EVENT_PICKED_DURATION, id);
        await eventApi.updateEvent(state.items[id], currentLocale);

        let { data } = await eventApi.getToBePlannedEvents(currentLocale);

        if (Object.keys(data).length === 0) {
          commit(actions.CLEAR_TO_BE_PLANNED_EVENT);
        }

        data.forEach(function (newEvent) {
          commit(actions.ARCHIVE_EVENT, newEvent.id);
          commit(actions.ADD_EVENT, newEvent);
        });
      } catch (e) {
        throw e.response ? e.response.data.message : e;
      }

      return;
    }

    try {
      const resp = await eventApi.scheduleMultiTimeSlot(
        id,
        eventTimeSlots,
        quantity,
        currentLocale
      );
      // Remove and ReAdd event on store
      commit(actions.ARCHIVE_EVENT, id);

      if (resp.data["parentId"]) {
        commit(actions.ARCHIVE_EVENT, resp.data["parentId"]);
      }

      let timeSlots = resp.data["time_slots"];
      commit(actions.ADD_EVENT, resp.data);
      timeSlots.forEach((timeSlot) => {
        checkSimultaneousTimeSlot(commit, timeSlot);
      });

      const { data } = await eventApi.processEventRules(id);

      commit(actions.FETCH_EVENT_RULES, extractEventRules(data));
    } catch (e) {
      throw e.response ? e.response.data.message : e;
    }
  },
  [actions.UNDO_SELECTED_EVENT]({ commit }, eventId) {
    eventApi.undoEvent(
      eventId,
      (undoEvent) => {
        commit(actions.UPDATE_EVENT, undoEvent);
      },
      currentLocale
    );
  },
  [actions.UNPLAN_EVENT]: async ({ commit, dispatch }, eventId) => {
    state.timeSlots
      .filter((timeSlot) => timeSlot.eventId === eventId)
      .forEach((timeSlot) =>
        getSimultaneousTimeSlot(timeSlot).forEach((simultaneousTs) => {
          commit(actions.REMOVE_SIMULTANEOUS_TIME_SLOT, {
            removedTimeSlot: timeSlot,
            simultaneousTimeSlot: simultaneousTs,
          });
        })
      );

    let response;
    try {
      response = await eventApi.unplanEvent(eventId, currentLocale);

      // Reload ParentEvent if pickedEvent
      if (state.items[eventId].parentId) {
        commit(actions.ARCHIVE_EVENT, state.items[eventId].parentId);
        let { data } = await eventApi.getToBePlannedEvents(currentLocale);
        data.forEach(function (newEvent) {
          commit(actions.ADD_EVENT, newEvent);
        });
      }
    } catch (e) {
      throw e.response.data.message;
    }

    commit(actions.UNPLAN_EVENT, eventId);

    dispatch(actions.FETCH_EVENT_RULES);

    return response;
  },
  [actions.UPDATE_EVENT]: ({ commit }, event) => {
    commit(actions.UPDATE_EVENT, event);
    return eventApi.updateEvent(event, currentLocale);
  },
  [actions.UPDATE_EVENT_COMMENT]: async ({ commit }, { eventId, comment }) => {
    const res = await eventApi.updateComment(eventId, comment, currentLocale);
    commit(actions.UPDATE_EVENT, res.data);
  },
  [actions.UPDATE_EVENT_DESCRIPTION]: async (
    { commit },
    { eventId, description }
  ) => {
    const res = await eventApi.updateDescription(
      eventId,
      description,
      currentLocale
    );
    commit(actions.UPDATE_EVENT, res.data);
  },
  [actions.UPDATE_EVENT_QUANTITY]: async (
    { commit },
    { eventId, quantity }
  ) => {
    const res = await eventApi.updateQuantity(eventId, quantity, currentLocale);
    commit(actions.UPDATE_EVENT, res.data);
  },
  [actions.UPDATE_EVENT_DEADLINE]: async (
    { commit },
    { eventId, deadline }
  ) => {
    const res = await eventApi.updateDeadline(eventId, deadline, currentLocale);
    commit(actions.UPDATE_EVENT, res.data);
  },
  [actions.UPDATE_EVENT_CONFIRMED_RS]: async (
    { commit },
    { eventId, confirmedRs }
  ) => {
    try {
      const res = await eventApi.updateConfirmedRs(
        eventId,
        confirmedRs,
        currentLocale
      );
      commit(actions.UPDATE_EVENT, res.data);
    } catch (e) {
      console.error(e);
      throw e.response.data.message;
    }
  },
  [actions.UPDATE_TIME_SLOT]: ({ commit }, timeSlot) => {
    if (!isInsideWorkContract(timeSlot)) {
      throw i18n.t("messages.error.notInsideWorkContract");
    }

    if (!canBePositionable(timeSlot)) {
      throw i18n.t("messages.error.blockedWeek");
    }

    if (endAfterMidnight(timeSlot)) {
      throw i18n.t("messages.error.eventAfterMidnight");
    }

    if (needLevel(state.items[timeSlot.eventId])) {
      throw i18n.t("messages.error.eventMove");
    }

    checkSimultaneousTimeSlot(commit, timeSlot);
    commit(actions.UPDATE_TIME_SLOT, timeSlot);

    return eventApi.updateTimeSlot(timeSlot, currentLocale);
  },
  [actions.SET_CALENDAR_OWNER]({ commit }, calendarOwner) {
    commit(actions.SET_CALENDAR_OWNER, calendarOwner);
  },
  [actions.SET_EVENT_SEARCH]({ commit }, eventSearch) {
    commit(actions.SET_EVENT_SEARCH, eventSearch);
  },
  /**
   * @param {any} context
   * @param {Week} week
   */
  [actions.LOAD_HOLIDAYS]: async ({ commit, state }, week) => {
    if (isNullish(week)) {
      return;
    }

    const years = getEffectiveYears(week);

    /** @type {Array<Promise<import('axios').AxiosResponse<Array<string>>>>} */
    const promises = [];
    /** @type {Array<number>} */
    const yearsToFetch = [];

    years.forEach((year) => {
      if (state.loadedHolidayYears.includes(year)) {
        return;
      }

      /** @type {Promise<import('axios').AxiosResponse<Array<string>>>} */
      const promise = securityApi.getHolidays(year);

      promises.push(promise);
      yearsToFetch.push(year);
    });

    const results = await Promise.allSettled(promises);

    results.forEach((result, index) => {
      if (result.status === "rejected") {
        return;
      }

      const year = yearsToFetch.at(index);

      commit(actions.LOAD_HOLIDAYS, { holidays: result.value.data, year });
    });
  },
  [actions.ALLOCATE_CREDIT]: ({ commit }, { creditValueId, duration }) => {
    commit(actions.ALLOCATE_CREDIT);
    return eventApi.updateCreditCustomer(
      creditValueId,
      duration,
      currentLocale
    );
  },
  [actions.SET_SUPERVISION_FILTERS_ENABLED]: (
    { commit },
    supervisionFiltersEnabled
  ) => {
    commit(actions.SET_SUPERVISION_FILTERS_ENABLED, supervisionFiltersEnabled);

    localStorage.setItem(
      "supervision-filters-enabled",
      JSON.stringify(supervisionFiltersEnabled)
    );
  },
  [actions.FETCH_DEPARTMENTS]: async ({ commit }) => {
    const { data } = await eventApi.getDepartments();

    commit(actions.FETCH_DEPARTMENTS, data);
  },
  [actions.FETCH_BUSINESS_GROUPS]: async ({ commit }) => {
    const { data } = await eventApi.getBusinessGroups();

    commit(actions.FETCH_BUSINESS_GROUPS, data);
  },
  [actions.FETCH_EVENT_RULES]: async ({ commit }) => {
    try {
      const { data } = await eventApi.getEventReferences();

      commit(actions.FETCH_EVENT_RULES, extractEventRules(data));
    } catch (error) {
      console.error(error);
    }
  },
  [actions.SET_CALENDAR_DATE]: ({ commit }, data) => {
    commit(actions.SET_CALENDAR_DATE, data);
  },
  [actions.SET_SELECTED_CALENDAR_EVENT]: (
    { commit },
    selectedCalendarEvent
  ) => {
    commit(actions.SET_SELECTED_CALENDAR_EVENT, selectedCalendarEvent);
  },
  [actions.UNSET_SELECTED_CALENDAR_EVENT]: ({ commit }) => {
    commit(actions.UNSET_SELECTED_CALENDAR_EVENT);
  },
  [actions.SCHEDULE_CALENDAR_EVENT]({ commit }, { start, end }) {
    commit(actions.SCHEDULE_CALENDAR_EVENT, { start, end });
  },
  [actions.FIX_SELECTED_CALENDAR_EVENT]: async ({ commit, dispatch }) => {
    const selectedCalendarEvent = state.selectedCalendarEvent;

    const selectedTs = selectedCalendarEvent?.timeSlot;

    if (!selectedTs) {
      return;
    }

    if (!isInsideWorkContract(selectedTs)) {
      throw i18n.t("messages.error.notInsideWorkContract");
    }

    if (!canBePositionable(selectedTs)) {
      throw i18n.t("messages.error.blockedWeek");
    }

    if (endAfterMidnight(selectedTs)) {
      throw i18n.t("messages.error.eventAfterMidnight");
    }

    if (needLevel(state.items[selectedTs.eventId])) {
      throw i18n.t("messages.error.eventMove");
    }

    try {
      if (state.items[selectedTs.eventId].state === "to_be_planned") {
        checkSimultaneousTimeSlot(commit, selectedTs);

        const { data } = await eventApi.createTimeSlot(
          selectedTs,
          currentLocale
        );

        commit(actions.PLAN_EVENT, selectedTs.eventId);
        commit(actions.DEFINE_TIME_SLOT_ID, {
          timeSlot: selectedTs,
          id: data.id,
        });

        dispatch(actions.FETCH_EVENT_RULES);
      } else {
        checkSimultaneousTimeSlot(commit, selectedTs);
        await eventApi.scheduleTimeSlot(
          selectedTs.id,
          selectedTs.date + " " + selectedTs.time,
          selectedTs.duration,
          currentLocale
        );

        const { data } = await eventApi.processEventRules(selectedTs.eventId);

        commit(actions.FETCH_EVENT_RULES, extractEventRules(data));
      }
    } catch (e) {
      console.error(e);
      let mess = e;

      if (e.response) {
        mess = e.response.data.message;
      }

      throw mess;
    }

    commit(actions.UNSET_SELECTED_CALENDAR_EVENT);
  },
  [actions.SET_IMPERSONATION_REDIRECT_PATH]({ commit }, path) {
    commit(actions.SET_IMPERSONATION_REDIRECT_PATH, path);
  },
};

function extractEventRules(data) {
  const eventRules = [];

  data.forEach((item) => {
    eventRules.push(item.eventId);
    eventRules.push(item.referencedEventId);
  });

  return eventRules;
}

function checkSimultaneousTimeSlot(commit, timeSlot) {
  // ensure that the event is registered in the store
  let currentTs = state.timeSlots.filter((ts) => ts.id === timeSlot.id);

  getSimultaneousTimeSlot(currentTs).forEach((simultaneousTs) => {
    if (
      !canBeSimultaneous(
        //state.items[timeSlot.eventId],
        currentTs,
        state.items[simultaneousTs.eventId]
      )
    ) {
      throw i18n.t("messages.error.eventOverlap");
    }

    commit(actions.DEFINE_SIMULTANEOUS_TIME_SLOT, {
      ts1: timeSlot,
      ts2: simultaneousTs,
    });
  });
}
